import { defineBlock } from "eddev/blocks"
import { cn } from "@utils/tw"
import { Col, Container, Grid } from "@components/layout/Grid"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react"
import { Swiper, SwiperSlide } from "@components/atoms/Swiper"
import * as LogosMap from "@components/atoms/Logo"
import { Heading } from "@components/atoms/Heading"
import { Typography } from "@components/atoms/Typography"
import { Chip } from "@components/atoms/Chip"
import { VSpace } from "@components/layout/Spacer"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { useBreakpointIsFrom } from "@hooks/useViewportDerived"

export const meta: BlockMeta = {
  title: "Quote swiper",
  category: "common",
  icon: "editor-quote",
  keywords: ["quote"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

const slideTime = 10000

export default defineBlock("general/quote-swiper", (props) => {
  const [swiperEl, setSwiperEl] = useState<any>()
  const [timerKey, setTimerKey] = useState(0)

  function handleSwiperInit(swiper: any) {
    setSwiperEl(swiper)
  }

  function handleSlideChange() {
    setTimerKey((prevKey) => prevKey + 1)
  }

  useEffect(() => {
    if (swiperEl) {
      swiperEl.on("slideChange", handleSlideChange)
    }
    return () => {
      if (swiperEl) {
        swiperEl.off("slideChange", handleSlideChange)
      }
    }
  }, [swiperEl])

  const isDesktop = useBreakpointIsFrom("desktop")
  return (
    <QuoteContainer>
      <Grid>
        {isDesktop && <Col initial={12} desktop={1} />}
        <Col initial={12} desktop={10}>
          <SwiperContainer>
            <style
              dangerouslySetInnerHTML={{
                __html: `
              .quote-swiper {
                div[class^='swiper-button'] {
                  top: auto;
                  bottom: 0;
                  right: auto;
                  left: 0;
                  width: 27px;
                  height: 27px;
                  color: #222222;
                  box-shadow: inset 0px 0px 0px 1px #222222;
                  border-radius: 6px;
                  transition: all .35s cubic-bezier(.19,1,.22,1);
                  transform: translateX(8%);

                  &:after {
                    font-size: 16px !important;
                  }

                  svg {
                    width: 5px;
                    height: auto;

                    path {
                      stroke: currentColor;
                      stroke-width: 2px;
                    }
                  }

                  &:hover {
                    background: #5E5E5E;
                    box-shadow: inset 0px 0px 0px 1px #5E5E5E;
                    color: #FFFFFF;
                  }

                  &:active {
                    background: #222222;
                    box-shadow: inset 0px 0px 0px 1px #222222;
                    color: #FFFFFF;
                  }

                  &:disabled {
                    background: transparent;
                    box-shadow: inset 0px 0px 0px 1px #8E8E8E;
                    color: #8E8E8E;
                  }
                }

                .swiper-button-next {
                  transform: translateX(calc(100% + 8px)) !important;
                }
              }
            `,
              }}
            />
            <Swiper
              className="quote-swiper"
              slidesPerView={1}
              autoplay={{
                delay: slideTime,
              }}
              navigation
              loop
              spaceBetween={24}
              onSwiper={handleSwiperInit}
            >
              {props.quotes?.map((quote, i) => {
                const LogoComponent = quote?.clubLogo ? LogosMap[quote.clubLogo as keyof typeof LogosMap] : null
                return (
                  <SwiperSlide key={i} style={{ display: "flex", height: "auto" }}>
                    <Quote>
                      <Left>
                        {quote?.clubLogo && (
                          <>
                            <Icon>{LogoComponent && <LogoComponent />}</Icon>
                            <VSpace initial={2} />
                          </>
                        )}
                        <Chip color="grey">
                          <ChipInner>
                            <Timer key={timerKey} /> Testimonial &mdash; {quote?.cite}
                          </ChipInner>
                        </Chip>
                        <VSpace initial={6} />
                        <Heading as="h3">{quote?.heading}</Heading>
                        <VSpace initial={4} />
                        <Typography as="p" variant="text-m" dangerouslySetInnerHTML={{ __html: quote?.quote ?? "" }} />
                      </Left>
                      <Right>
                        {quote?.image ? <ResponsiveImage {...quote.image} aspect={408 / 288} /> : <LogosMap.Logo />}
                      </Right>
                    </Quote>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </SwiperContainer>
        </Col>
      </Grid>
    </QuoteContainer>
  )
})

const QuoteContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => <Container ref={ref} className={cn("my-6", className)} {...props} />,
)
QuoteContainer.displayName = "QuoteContainer"

const SwiperContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("py-5 px-6", className)} {...props} />
))
SwiperContainer.displayName = "SwiperContainer"

const Quote = forwardRef<HTMLQuoteElement, ComponentPropsWithoutRef<"blockquote">>(({ className, ...props }, ref) => (
  <blockquote
    ref={ref}
    className={cn("flex w-full gap-7 flex-col md:flex-row md:justify-between", className)}
    {...props}
  />
))
Quote.displayName = "Quote"

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col items-start w-full pb-[40px] order-2 md:w-[calc(100%*(3/5))] md:order-1", className)}
    {...props}
  />
))
Left.displayName = "Left"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "relative rounded-[6px] overflow-hidden w-full bg-grey order-1 h-0 pt-[calc(100%*(288/408))]",
      "[&>div]:absolute [&>div]:top-0 [&>div]:inset-0 [&>div]:h-full",
      "[&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white",
      "md:w-[calc(100%*(2/5))] md:pt-[calc((100%*(2/5))*(288/408))] md:order-2",
      className,
    )}
    {...props}
  />
))
Right.displayName = "Right"

const Icon = forwardRef<HTMLElement, ComponentPropsWithoutRef<"i">>(({ className, ...props }, ref) => (
  <i ref={ref} className={cn("w-[48px] h-[48px] [&_svg]:w-full [&_svg]:h-full", className)} {...props} />
))
Icon.displayName = "Icon"

const ChipInner = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
))
ChipInner.displayName = "ChipInner"

// Ported from a bespoke `pieAniAfter` keyframes() block (rotate + black/
// light-grey background swap) onto the shared `pie-timer` keyframe already
// added to tailwind.config.ts for timed-cards.tsx's identical countdown-pie
// need — consolidating both onto the one keyframe/color-pair (black/white)
// rather than keeping a second near-duplicate. Duration is `slideTime + 500`
// (10000 + 500 = 10500ms), matching the original's animation length.
const Timer = () => (
  <div
    className={
      "relative w-[12px] h-[12px] border border-white shadow-[inset_0_0_1px_white] rounded-full bg-transparent " +
      "[background-image:linear-gradient(90deg,#222222_50%,#ffffff_50%)] overflow-hidden mr-2 " +
      "after:absolute after:left-1/2 after:w-1/2 after:h-full after:content-[''] after:bg-black after:origin-[0_50%] " +
      "after:animate-[pie-timer_10500ms_linear_infinite]"
    }
  />
)
